/**
 * wall_contact.ts — what a craft meeting a wall SOUNDS and FEELS like (physics follow-up A, 2026-10-05). PURE.
 *
 * `stepHover` already separates the two halves of a wall contact (vehicle review 2026-09-16): `impact` is non-zero
 * only on the tick contact BEGINS and carries the CLOSING speed; `contact` stays true through the grind. Until now the
 * module turned both into gamepad rumble only — a hovercraft hitting or grinding a wall was SILENT (`audit-feel.md` G4).
 * This file maps the readout onto:
 *
 *   · an IMPACT one-shot (`sfx_hover_impact`, four synthesised takes — a hollow metal / composite thunk): gain on a LOG
 *     scale of the closing speed (loudness is perceived logarithmically — a 5° graze at cruise closes at ~3 m/s and a
 *     head-on at ~35 m/s; on a linear scale the graze would be inaudible next to the crash, on a log scale it is a
 *     clear knock a third as loud), the pitch a touch UP for a lighter craft (smaller panels ring higher; ±10 %), and
 *     an 80 ms cooldown so a craft rattling along a barrier chain is one thunk per real hit, never a machine gun.
 *   · a SCRAPE loop (`sfx_hover_scrape`, scoped to the ride) while contact holds: gain and playback rate follow the
 *     speed ALONG the wall (after the impact the integrator has cancelled the into-wall component, so the velocity IS
 *     the tangential speed); pressed against a wall and not sliding, it is silent. It fades out over ~0.3 s when the
 *     contact ends — after a short HOLD that rides over the one-tick gaps a three-point hull probe leaves at a corner.
 *   · the FEEL layer: a small camera kick (a nod down, by severity) and trauma for the opt-in shake. No hit-stop —
 *     freezing a 35 m/s craft's picture would read as a stall, not a blow.
 *
 * Fixed-tick and frame-rate independent: the loop's smoothing is the runtime's own time ramp (`LoopHandle.set(gain,
 * rate, rampS)`), so the step returns a TARGET and a ramp, never a per-call fraction.
 */

// ── The impact one-shot ───────────────────────────────────────────────────────────────────────────────────────────
/** Below this closing speed a touch makes no knock (a craft nudging a wall at a crawl). */
export const WALL_IMPACT_MIN_MPS = 0.8;
/** The log curve's knee: the gain grows with ln(1 + v / V0). */
export const WALL_IMPACT_V0_MPS = 2;
/** The closing speed of the loudest thunk (a boosted head-on; cruise is 27.5–47.5 m/s, boost ≤ ~70). */
export const WALL_IMPACT_FULL_MPS = 40;
/** Gain at the threshold and at full severity (the sfx bus gain, before the vehicles bus). */
export const WALL_IMPACT_GAIN_MIN = 0.14;
export const WALL_IMPACT_GAIN_MAX = 0.8;
/** One thunk per real hit: a second impact inside this window is the same rattle. */
export const WALL_IMPACT_COOLDOWN_MS = 80;
/** A lighter craft rings higher: rate = (REF / mass)^EXP, clamped. */
export const WALL_PITCH_EXP = 0.18;
export const WALL_PITCH_MIN = 0.9;
export const WALL_PITCH_MAX = 1.12;
/** ± this much deterministic jitter per hit, on top of the take's own variety (four takes in a shuffle bag). */
export const WALL_PITCH_JITTER = 0.03;

/** Relative hull mass (tonnes) per hovercraft chassis — the PITCH lever only, never the physics (the integrator has no
 *  mass). From the catalog's own descriptions: the skiff is "a welded pallet", the dune runner "boxy, stubborn". */
export const HOVER_MASS_T: Readonly<Record<string, number>> = {
  hover_skiff: 0.9, hover_dune: 2.2, hover_falcon: 1.1, hover_raptor: 1.6, hover_tempest: 1.4,
  hover_mule: 2.6, hover_punt: 0.5, hover_strider: 1.3, hover_lark: 1, hover_wasp: 0.7,
  hover_ox: 3, hover_needle: 0.9, hover_marshal: 1.7, hover_courser: 1.8, hover_kite: 1.2,
};
export const WALL_MASS_REF_T = 1.4;
/** A ship's hull is a heavy one: the thunk drops toward the floor of the band. */
export const SHIP_MASS_T = 3.5;

/** 0..1: how hard a hit was, on the log scale (0 under the threshold). */
export function impactSeverity(closingMps: number): number {
  if (!(closingMps >= WALL_IMPACT_MIN_MPS)) return 0;
  const f = Math.log1p(closingMps / WALL_IMPACT_V0_MPS) / Math.log1p(WALL_IMPACT_FULL_MPS / WALL_IMPACT_V0_MPS);
  return Math.min(1, Math.max(0, f));
}

/** The thunk for a closing speed and a hull mass; `jitter01` ∈ [0, 1) picks the per-hit pitch jitter. Null = silent. */
export function wallImpactSound(closingMps: number, massT: number, jitter01 = 0.5): { gain: number; rate: number } | null {
  const sev = impactSeverity(closingMps);
  if (sev <= 0) return null;
  const gain = WALL_IMPACT_GAIN_MIN + (WALL_IMPACT_GAIN_MAX - WALL_IMPACT_GAIN_MIN) * sev;
  const m = Number.isFinite(massT) && massT > 0 ? massT : WALL_MASS_REF_T;
  const base = Math.min(WALL_PITCH_MAX, Math.max(WALL_PITCH_MIN, Math.pow(WALL_MASS_REF_T / m, WALL_PITCH_EXP)));
  const j = Number.isFinite(jitter01) ? Math.min(1, Math.max(0, jitter01)) : 0.5;
  return { gain, rate: base * (1 + (j * 2 - 1) * WALL_PITCH_JITTER) };
}

/** The cooldown gate: returns true (and arms) when an impact may sound at `nowMs`. */
export interface ImpactGate { lastMs: number; played: number; suppressed: number }
export function newImpactGate(): ImpactGate { return { lastMs: -Infinity, played: 0, suppressed: 0 }; }
export function impactGateOpen(g: ImpactGate, nowMs: number): boolean {
  if (nowMs - g.lastMs < WALL_IMPACT_COOLDOWN_MS) { g.suppressed++; return false; }
  g.lastMs = nowMs; g.played++;
  return true;
}

/** A deterministic 0..1 from a hit counter (the pitch jitter must not depend on Math.random — the harness pins it). */
export function hitJitter(n: number): number {
  let h = (Math.imul(n | 0, 0x9e3779b1) ^ 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

// ── The feel layer ────────────────────────────────────────────────────────────────────────────────────────────────
/** A full-severity crash nods the camera this far (rad; a blow TAKEN on foot is 0.045). */
export const WALL_KICK_PITCH = 0.035;
/** …and adds this much trauma (the shake is opt-in and scaled by the camera-motion row). */
export const WALL_TRAUMA = 0.32;
/** Under this severity (≈ 3 m/s closing) a hit is felt in the pad and heard, but does not move the camera (a graze). */
export const WALL_KICK_MIN_SEV = 0.3;
export function wallImpactFeel(closingMps: number): { pitch: number; trauma: number } {
  const sev = impactSeverity(closingMps);
  if (sev < WALL_KICK_MIN_SEV) return { pitch: 0, trauma: 0 };
  return { pitch: -WALL_KICK_PITCH * sev, trauma: WALL_TRAUMA * sev * sev };
}

// ── The scrape loop ───────────────────────────────────────────────────────────────────────────────────────────────
/** Slower than this along the wall: silent (pressed against it, not sliding). */
export const SCRAPE_MIN_MPS = 0.6;
/** The speed along the wall at which the grind is at full gain and top rate. */
export const SCRAPE_FULL_MPS = 30;
export const SCRAPE_GAIN_MAX = 0.55;
export const SCRAPE_RATE_MIN = 0.78;
export const SCRAPE_RATE_MAX = 1.3;
/** Ramps (s): the grind bites at once and lets go softly. */
export const SCRAPE_ATTACK_S = 0.05;
export const SCRAPE_RELEASE_S = 0.28;
/** A contact gap shorter than this keeps the grind alive (a corner probe drops out for a tick or two). */
export const SCRAPE_HOLD_S = 0.08;

export interface ScrapeState {
  /** Seconds since the last tick in contact (Infinity before the first contact). */
  sinceContactS: number;
  /** The last target gain / rate handed to the loop. */
  gain: number;
  rate: number;
  /** Diagnostics. */
  grinds: number;
}
export function newScrape(): ScrapeState { return { sinceContactS: Infinity, gain: 0, rate: SCRAPE_RATE_MIN, grinds: 0 }; }

/** The grind's target for a speed along the wall (0 under SCRAPE_MIN_MPS). */
export function scrapeTarget(alongMps: number): { gain: number; rate: number } {
  if (!(alongMps > SCRAPE_MIN_MPS)) return { gain: 0, rate: SCRAPE_RATE_MIN };
  const f = Math.min(1, (alongMps - SCRAPE_MIN_MPS) / (SCRAPE_FULL_MPS - SCRAPE_MIN_MPS));
  // √ on the gain: a slow grind is already clearly a grind; the rate climbs linearly (pitch reads speed).
  return { gain: SCRAPE_GAIN_MAX * Math.sqrt(f), rate: SCRAPE_RATE_MIN + (SCRAPE_RATE_MAX - SCRAPE_RATE_MIN) * f };
}

/** One fixed tick of the grind: what to hand the loop (`set(gain, rate, ramp)`). Mutates the state. */
export function scrapeStep(s: ScrapeState, contact: boolean, alongMps: number, dt: number): { gain: number; rate: number; ramp: number } {
  if (contact) {
    if (!(s.sinceContactS < SCRAPE_HOLD_S)) s.grinds++;
    s.sinceContactS = 0;
    const t = scrapeTarget(alongMps);
    s.gain = t.gain; s.rate = t.rate;
    return { gain: t.gain, rate: t.rate, ramp: SCRAPE_ATTACK_S };
  }
  s.sinceContactS += Math.max(0, Number.isFinite(dt) ? dt : 0);
  if (s.sinceContactS < SCRAPE_HOLD_S) return { gain: s.gain, rate: s.rate, ramp: SCRAPE_ATTACK_S };
  s.gain = 0;
  return { gain: 0, rate: s.rate, ramp: SCRAPE_RELEASE_S };
}
